import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  CalendarIcon,
  MapPinIcon,
  IdentificationIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  TrophyIcon,
  ChartBarIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { toProperCase, isFieldEvent, eventSortKey, parseTime, formatTime, formatTimePadded, formatTimeForDisplay, formatResultForDisplay } from '../utils/formatUtils';

const Profile = () => {
  const { userProfile, currentUser, refreshUserProfile, changePassword } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [events, setEvents] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState('');
  const [personalBests, setPersonalBests] = useState({});
  const [seasonBests, setSeasonBests] = useState({});
  const [myResults, setMyResults] = useState([]);
  const [showAddResult, setShowAddResult] = useState(false);
  const [competitionsList, setCompetitionsList] = useState([]);
  const [addResultForm, setAddResultForm] = useState({ competition_id: '', event_code: '', result_value: '', season: '' });
  const [savingPbSb, setSavingPbSb] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact_number: '',
    date_of_birth: '',
    address: '',
    ic_number: '',
    nationality: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    medical_conditions: '',
    allergies: '',
    coach_name: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Fetch events and seasons from API
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    let cancelled = false;
    (async () => {
      try {
        const [eventsRes, seasonsRes] = await Promise.all([
          fetch(`${apiUrl}/api/competitions/events`),
          fetch(`${apiUrl}/api/competitions/seasons`)
        ]);
        if (cancelled) return;
        if (eventsRes.ok) {
          const d = await eventsRes.json();
          if (d.success && Array.isArray(d.events)) {
            const byCode = {};
            d.events.forEach((ev) => {
              const code = ev.event_code || ev.event || ev.id;
              if (code && !byCode[code]) byCode[code] = { event_code: code, event: ev.event || ev.event_code || code, type: ev.type };
            });
            setEvents(Object.values(byCode));
          }
        }
        if (seasonsRes.ok) {
          const d = await seasonsRes.json();
          if (d.success && Array.isArray(d.seasons)) {
            setSeasons(d.seasons);
            if (d.current_season) setCurrentSeason(d.current_season);
            else if (d.seasons.length) setCurrentSeason(d.seasons[Math.floor(d.seasons.length / 2)]);
          }
        }
      } catch (e) {
        if (!cancelled) console.error('Error fetching events/seasons:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchMyResults = React.useCallback(async () => {
    if (!currentUser) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const token = await currentUser.getIdToken();
      const res = await fetch(`${apiUrl}/api/results`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        if (d.success && Array.isArray(d.results)) setMyResults(d.results);
      }
    } catch (e) {
      console.error('Error fetching results:', e);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchMyResults();
  }, [fetchMyResults]);

  useEffect(() => {
    if (showAddResult && competitionsList.length === 0) {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      fetch(`${apiUrl}/api/competitions/list`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (d?.success && Array.isArray(d.competitions)) setCompetitionsList(d.competitions); })
        .catch((e) => console.error('Error fetching competitions:', e));
    }
  }, [showAddResult, competitionsList.length]);

  const handleAddResultSubmit = async (e) => {
    e.preventDefault();
    if (!addResultForm.competition_id || !addResultForm.event_code || !addResultForm.result_value || !addResultForm.season) {
      toast.error('Please fill in competition, event, result and season.');
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const token = await currentUser.getIdToken();
      const res = await fetch(`${apiUrl}/api/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(addResultForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.season_best_updated ? 'Result saved and season best updated!' : 'Result saved.');
        setShowAddResult(false);
        setAddResultForm({ competition_id: '', event_code: '', result_value: '', season: '' });
        fetchMyResults();
        refreshUserProfile();
      } else {
        toast.error(data.error || 'Failed to save result');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save result');
    }
  };

  useEffect(() => {
    if (userProfile) {
      setFormData({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        email: userProfile.email || currentUser?.email || '',
        contact_number: userProfile.contact_number || '',
        date_of_birth: userProfile.date_of_birth || '',
        address: userProfile.address || '',
        ic_number: userProfile.ic_number || '',
        nationality: userProfile.nationality || userProfile.Nationality || '',
        emergency_contact_name: userProfile.emergency_contact?.name || '',
        emergency_contact_phone: userProfile.emergency_contact?.phone || '',
        emergency_contact_relationship: userProfile.emergency_contact?.relationship || '',
        medical_conditions: userProfile.medical_conditions || '',
        allergies: userProfile.allergies || '',
        coach_name: (userProfile.competition_profile || {}).coach_name || userProfile.coach_name || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      const cp = userProfile.competition_profile || {};
      setPersonalBests(cp.personal_bests || {});
      const sb = cp.season_bests || {};
      const cur = userProfile.current_season || currentSeason;
      const isNested = Object.keys(sb).length > 0 && Object.values(sb).some((v) => typeof v === 'object' && v !== null);
      if (isNested) {
        setSeasonBests(sb);
      } else if (cur && Object.keys(sb).length > 0) {
        setSeasonBests({ [cur]: sb });
      } else {
        setSeasonBests(sb);
      }
      if (userProfile.current_season && !currentSeason) setCurrentSeason(userProfile.current_season);
    }
  }, [userProfile, currentUser, currentSeason]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.first_name.trim()) errors.push('First name is required');
    if (!formData.last_name.trim()) errors.push('Last name is required');
    if (!formData.email.trim()) errors.push('Email is required');
    if (!formData.contact_number.trim()) errors.push('Contact number is required');
    if (!formData.ic_number.trim()) errors.push('IC number is required');
    if (!formData.nationality.trim()) errors.push('Nationality is required');

    // Validate IC number format (3 numbers + 1 letter)
    if (formData.ic_number && !/^[0-9]{3}[A-Za-z]$/.test(formData.ic_number)) {
      errors.push('IC number must be 3 numbers followed by 1 letter (e.g., 123A)');
    }

    // Validate email format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }

    // Validate contact number (Singapore format)
    if (formData.contact_number && !/^[89][0-9]{7}$/.test(formData.contact_number.replace(/\s/g, ''))) {
      errors.push('Please enter a valid Singapore contact number');
    }

    // Password validation (only if changing password)
    if (formData.new_password || formData.confirm_password) {
      if (!formData.current_password) {
        errors.push('Current password is required to change password');
      }
      if (formData.new_password.length < 6) {
        errors.push('New password must be at least 6 characters');
      }
      if (formData.new_password !== formData.confirm_password) {
        errors.push('New passwords do not match');
      }
    }

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    setIsLoading(true);
    try {
            // Handle password change separately if provided
            if (formData.new_password) {
              await changePassword(formData.current_password, formData.new_password);
            }

      // Update profile data (excluding password fields)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const token = await currentUser.getIdToken();
      
      const updateData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        contact_number: formData.contact_number.trim(),
        date_of_birth: formData.date_of_birth || null,
        address: formData.address.trim() || null,
        ic_number: formData.ic_number.trim(),
        nationality: formData.nationality.trim() || null,
        emergency_contact: {
          name: formData.emergency_contact_name.trim() || null,
          phone: formData.emergency_contact_phone.trim() || null,
          relationship: formData.emergency_contact_relationship.trim() || null
        },
        medical_conditions: formData.medical_conditions.trim() || null,
        allergies: formData.allergies.trim() || null,
        competition_profile: {
          ...(userProfile.competition_profile || {}),
          personal_bests: personalBests,
          season_bests: (() => {
            const sb = { ...seasonBests };
            if (sb.current && (currentSeason || seasons[0])) {
              const key = currentSeason || seasons[0];
              sb[key] = { ...(sb[key] || {}), ...sb.current };
              delete sb.current;
            }
            return sb;
          })(),
          coach_name: formData.coach_name.trim() || null
        }
      };

      const response = await fetch(`${apiUrl}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await refreshUserProfile();
        setIsEditing(false);
        
        // Show success message
        if (formData.new_password) {
          toast.success('Profile and password updated successfully!');
        } else {
          toast.success('Profile updated successfully!');
        }
        
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          current_password: '',
          new_password: '',
          confirm_password: ''
        }));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      
      // If it's a password change error, don't show generic profile error
      if (formData.new_password && error.code && error.code.startsWith('auth/')) {
        // Password error already handled in changePassword function
        return;
      }
      
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const { trackEvents, fieldEvents } = useMemo(() => {
    const name = (ev) => (ev.event || ev.event_code || '').trim();
    const track = events.filter((ev) => !isFieldEvent(ev)).sort((a, b) => {
      const ka = eventSortKey(a), kb = eventSortKey(b);
      if (ka !== kb) return ka - kb;
      return name(a).localeCompare(name(b));
    });
    const field = events.filter((ev) => isFieldEvent(ev)).sort((a, b) => {
      const ka = eventSortKey(a), kb = eventSortKey(b);
      if (ka !== kb) return ka - kb;
      return name(a).localeCompare(name(b));
    });
    return { trackEvents: track, fieldEvents: field };
  }, [events]);

  // Event codes that already have PB or SB (for "Add PB/SB" dropdown: only show events not in this set)
  const eventCodesWithData = useMemo(() => {
    const codes = new Set(Object.keys(personalBests));
    Object.keys(seasonBests).forEach((k) => {
      const v = seasonBests[k];
      if (v && typeof v === 'object' && !Array.isArray(v)) Object.keys(v).forEach((c) => codes.add(c));
      else codes.add(k);
    });
    return codes;
  }, [personalBests, seasonBests]);

  // Entries to show in PB/SB table: one per event the user has added, grouped Track then Field, sorted smallest to largest then name
  const pbSbEntries = useMemo(() => {
    const evByCode = {};
    events.forEach((ev) => { const c = ev.event_code || ev.event; if (c) evByCode[c] = ev; });
    const track = [];
    const field = [];
    eventCodesWithData.forEach((code) => {
      const ev = evByCode[code];
      if (!ev) return;
      const entry = { ev, code };
      if (isFieldEvent(ev)) field.push(entry);
      else track.push(entry);
    });
    const cmp = (a, b) => {
      const ka = eventSortKey(a.ev), kb = eventSortKey(b.ev);
      if (ka !== kb) return ka - kb;
      return (a.ev.event || a.ev.event_code || '').localeCompare(b.ev.event || b.ev.event_code || '');
    };
    track.sort(cmp);
    field.sort(cmp);
    return { track, field };
  }, [events, eventCodesWithData]);

  // View mode: entries and values from DB (userProfile) so table always shows what's saved
  const { pbSbEntriesFromProfile, eventCodesWithDataFromProfile } = useMemo(() => {
    const cp = userProfile?.competition_profile || {};
    const pb = cp.personal_bests || {};
    const sb = cp.season_bests || {};
    const codes = new Set(Object.keys(pb));
    Object.keys(sb).forEach((k) => {
      const v = sb[k];
      if (v && typeof v === 'object' && !Array.isArray(v)) Object.keys(v).forEach((c) => codes.add(c));
      else codes.add(k);
    });
    const evByCode = {};
    events.forEach((ev) => { const c = ev.event_code || ev.event; if (c) evByCode[c] = ev; });
    const track = [];
    const field = [];
    codes.forEach((code) => {
      const ev = evByCode[code];
      if (!ev) return;
      const entry = { ev, code };
      if (isFieldEvent(ev)) field.push(entry);
      else track.push(entry);
    });
    const cmp = (a, b) => {
      const ka = eventSortKey(a.ev), kb = eventSortKey(b.ev);
      if (ka !== kb) return ka - kb;
      return (a.ev.event || a.ev.event_code || '').localeCompare(b.ev.event || b.ev.event_code || '');
    };
    track.sort(cmp);
    field.sort(cmp);
    return { pbSbEntriesFromProfile: { track, field }, eventCodesWithDataFromProfile: codes };
  }, [userProfile, events]);

  const [addForm, setAddForm] = useState({ event_code: '', pb: '', sb: '' });
  const [isPbSbEditing, setIsPbSbEditing] = useState(false);

  const handleAddPbSb = () => {
    const code = addForm.event_code?.trim();
    if (!code) {
      toast.error('Select an event to add.');
      return;
    }
    const pb = (addForm.pb ?? '').toString().trim();
    const sb = (addForm.sb ?? '').toString().trim();
    if (!pb && !sb) {
      toast.error('Enter at least PB or SB.');
      return;
    }
    setPersonalBests((prev) => (pb ? { ...prev, [code]: pb } : prev));
    setSeasonBests((prev) => {
      const seasonKey = currentSeason || 'current';
      return sb ? { ...prev, [seasonKey]: { ...(prev[seasonKey] || {}), [code]: sb } } : prev;
    });
    setAddForm({ event_code: '', pb: '', sb: '' });
    toast.success('Added. Click "Save PB & SB" to save.');
  };

  const handleRemovePbSb = (code) => {
    setPersonalBests((prev) => { const next = { ...prev }; delete next[code]; return next; });
    setSeasonBests((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        const v = prev[k];
        if (v && typeof v === 'object' && !Array.isArray(v)) {
          const inner = { ...v }; delete inner[code]; next[k] = inner;
        } else if (k !== code) next[k] = v;
      });
      return next;
    });
  };

  const handleSavePbSb = async () => {
    if (!currentUser) return;
    setSavingPbSb(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const token = await currentUser.getIdToken();
      const sbResolved = (() => {
        const sb = { ...seasonBests };
        if (sb.current && (currentSeason || seasons[0])) {
          const key = currentSeason || seasons[0];
          sb[key] = { ...(sb[key] || {}), ...sb.current };
          delete sb.current;
        }
        return sb;
      })();
      const padTimes = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k, formatTimePadded(v)]));
      const sbPadded = Object.fromEntries(Object.entries(sbResolved).map(([k, v]) =>
        typeof v === 'object' && v !== null && !Array.isArray(v) ? [k, padTimes(v)] : [k, formatTimePadded(v)]
      ));
      const response = await fetch(`${apiUrl}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          competition_profile: {
            personal_bests: padTimes(personalBests),
            season_bests: sbPadded
          }
        })
      });
      if (response.ok) {
        await refreshUserProfile();
        setIsPbSbEditing(false);
        toast.success('PB & SB saved.');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to save PB & SB');
      }
    } catch (error) {
      console.error('Error saving PB/SB:', error);
      toast.error('Failed to save PB & SB. Please try again.');
    } finally {
      setSavingPbSb(false);
    }
  };

  const revertPbSbFromProfile = () => {
    if (!userProfile) return;
    const cp = userProfile.competition_profile || {};
    setPersonalBests(cp.personal_bests || {});
    const sb = cp.season_bests || {};
    const cur = userProfile.current_season || currentSeason;
    const isNested = Object.keys(sb).length > 0 && Object.values(sb).some((v) => typeof v === 'object' && v !== null);
    if (isNested) setSeasonBests(sb);
    else if (cur && Object.keys(sb).length > 0) setSeasonBests({ [cur]: sb });
    else setSeasonBests(sb);
  };

  const handleCancelPbSb = () => {
    setIsPbSbEditing(false);
  };

  const handleEditPbSb = () => {
    revertPbSbFromProfile();
    setIsPbSbEditing(true);
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (userProfile) {
      revertPbSbFromProfile();
      setFormData({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        email: userProfile.email || currentUser?.email || '',
        contact_number: userProfile.contact_number || '',
        date_of_birth: userProfile.date_of_birth || '',
        address: userProfile.address || '',
        ic_number: userProfile.ic_number || '',
        nationality: userProfile.nationality || userProfile.Nationality || '',
        emergency_contact_name: userProfile.emergency_contact?.name || '',
        emergency_contact_phone: userProfile.emergency_contact?.phone || '',
        emergency_contact_relationship: userProfile.emergency_contact?.relationship || '',
        medical_conditions: userProfile.medical_conditions || '',
        allergies: userProfile.allergies || '',
        coach_name: (userProfile.competition_profile || {}).coach_name || userProfile.coach_name || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    }
    setIsEditing(false);
  };

  const formatContactNumber = (value) => {
    // Format Singapore phone number: 81234567 -> 8123 4567
    const cleaned = value.replace(/\s/g, '');
    if (cleaned.length <= 4) return cleaned;
    return cleaned.slice(0, 4) + ' ' + cleaned.slice(4, 8);
  };

  const handleContactNumberChange = (e) => {
    const formatted = formatContactNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      contact_number: formatted
    }));
  };

  const handleEmergencyContactPhoneChange = (e) => {
    const formatted = formatContactNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      emergency_contact_phone: formatted
    }));
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-gray-900 via-secondary-900 to-primary-900">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Profile
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Manage your personal information and account settings
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Action Buttons */}
        <div className="mb-8 flex justify-end">
          <div className="flex space-x-3">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Profile
              </button>
            ) : (
              <div className="flex space-x-3">
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-primary-600" />
              Personal Information
            </h2>
            
            {/* UniqueID Display */}
            {userProfile?.unique_id && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-primary-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Your Unique ID</p>
                    <p className="text-lg font-bold text-blue-800">{userProfile.unique_id}</p>
                    <p className="text-xs text-blue-700 mt-1">This ID will be used for competition registrations</p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter your last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="Enter your email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Contact Number *
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  <input
                    type="tel"
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleContactNumberChange}
                    disabled={!isEditing}
                    maxLength="9"
                    className="w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="8123 4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Date of Birth
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    className="w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  IC Number (Last 4 digits) *
                </label>
                <div className="relative">
                  <IdentificationIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  <input
                    type="text"
                    name="ic_number"
                    value={formData.ic_number}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    maxLength="4"
                    pattern="[0-9]{3}[A-Za-z]"
                    className="w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                    placeholder="123A"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Nationality *
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="e.g., Singapore"
                />
                <p className="text-xs text-gray-500 mt-1">Required for competition registration.</p>
              </div>
            </div>
            <div className="mt-4 sm:mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Address
              </label>
              <div className="relative">
                <MapPinIcon className="absolute left-3 top-3 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows={3}
                  className="w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base resize-none"
                  placeholder="Enter your address"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <PhoneIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-red-600" />
              Emergency Contact
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Enter emergency contact name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleEmergencyContactPhoneChange}
                  disabled={!isEditing}
                  maxLength="9"
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="8123 4567"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Relationship
                </label>
                <select
                  name="emergency_contact_relationship"
                  value={formData.emergency_contact_relationship}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  <option value="">Select relationship</option>
                  <option value="parent">Parent</option>
                  <option value="spouse">Spouse</option>
                  <option value="sibling">Sibling</option>
                  <option value="friend">Friend</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Personal Bests (PB) & Season Bests (SB) */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                  <TrophyIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-primary-600" />
                  Personal Bests (PB) &amp; Season Bests (SB)
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {isPbSbEditing ? 'Add or edit events below, then save.' : 'Your saved PBs and SBs. Click Edit to change.'}
                </p>
              </div>
              {!isPbSbEditing ? (
                <button
                  type="button"
                  onClick={handleEditPbSb}
                  className="inline-flex items-center px-4 py-2 border border-primary-600 text-sm font-medium rounded-lg text-primary-600 bg-white hover:bg-primary-50 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit PB &amp; SB
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCancelPbSb}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              )}
            </div>
            <div className="space-y-4">
              {seasons.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Season for SB</label>
                  <select
                    value={currentSeason}
                    onChange={(e) => setCurrentSeason(e.target.value)}
                    disabled={!isPbSbEditing}
                    className="w-full sm:w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-600"
                  >
                    {seasons.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Add PB/SB: only in edit mode */}
              {isPbSbEditing && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                <h3 className="text-sm font-medium text-gray-800">Add PB/SB</h3>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Event</label>
                    <select
                      value={addForm.event_code}
                      onChange={(e) => setAddForm((p) => ({ ...p, event_code: e.target.value, pb: '', sb: '' }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select event</option>
                      <optgroup label="Track">
                        {trackEvents.filter((ev) => !eventCodesWithData.has(ev.event_code || ev.event)).map((ev) => (
                          <option key={ev.event_code || ev.event} value={ev.event_code || ev.event}>{toProperCase(ev.event || ev.event_code)}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Field">
                        {fieldEvents.filter((ev) => !eventCodesWithData.has(ev.event_code || ev.event)).map((ev) => (
                          <option key={ev.event_code || ev.event} value={ev.event_code || ev.event}>{toProperCase(ev.event || ev.event_code)}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  {addForm.event_code && (() => {
                    const ev = events.find((e) => (e.event_code || e.event) === addForm.event_code);
                    const isField = ev && isFieldEvent(ev);
                    const pbTime = parseTime(addForm.pb || '');
                    const sbTime = parseTime(addForm.sb || '');
                    return (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">PB</label>
                          {isField ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={addForm.pb}
                              onChange={(e) => setAddForm((p) => ({ ...p, pb: e.target.value }))}
                              placeholder="e.g. 6.50"
                              className="w-full max-w-[100px] px-2 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <input type="text" inputMode="numeric" placeholder="hh" maxLength={2} value={pbTime.hh} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, pb: formatTime({ ...parseTime(p.pb), hh: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Hours" />
                              <input type="text" inputMode="numeric" placeholder="mm" maxLength={2} value={pbTime.mm} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, pb: formatTime({ ...parseTime(p.pb), mm: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Minutes" />
                              <input type="text" inputMode="numeric" placeholder="ss" maxLength={2} value={pbTime.ss} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, pb: formatTime({ ...parseTime(p.pb), ss: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Seconds" />
                              <input type="text" inputMode="numeric" placeholder="ms" maxLength={2} value={pbTime.ms} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, pb: formatTime({ ...parseTime(p.pb), ms: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Hundredths" />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">SB</label>
                          {isField ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={addForm.sb}
                              onChange={(e) => setAddForm((p) => ({ ...p, sb: e.target.value }))}
                              placeholder="e.g. 6.50"
                              className="w-full max-w-[100px] px-2 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <input type="text" inputMode="numeric" placeholder="hh" maxLength={2} value={sbTime.hh} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, sb: formatTime({ ...parseTime(p.sb), hh: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Hours" />
                              <input type="text" inputMode="numeric" placeholder="mm" maxLength={2} value={sbTime.mm} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, sb: formatTime({ ...parseTime(p.sb), mm: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Minutes" />
                              <input type="text" inputMode="numeric" placeholder="ss" maxLength={2} value={sbTime.ss} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, sb: formatTime({ ...parseTime(p.sb), ss: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Seconds" />
                              <input type="text" inputMode="numeric" placeholder="ms" maxLength={2} value={sbTime.ms} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddForm((p) => ({ ...p, sb: formatTime({ ...parseTime(p.sb), ms: v }, { pad: false }) })); }} className="min-w-[2.5rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-md text-center" title="Hundredths" />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleAddPbSb}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" /> Add
                        </button>
                      </>
                    );
                  })()}
                </div>
                {events.length > 0 && eventCodesWithData.size === 0 && (
                  <p className="text-xs text-gray-500">Select an event above and enter at least PB or SB, then click Add.</p>
                )}
              </div>
              )}

              {/* PB/SB entries: view mode = from DB (userProfile); edit mode = from local state */}
              <div className="space-y-6">
                {(isPbSbEditing ? pbSbEntries.track : pbSbEntriesFromProfile.track).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Track</h3>
                    <div className="space-y-4">
                      {(isPbSbEditing ? pbSbEntries.track : pbSbEntriesFromProfile.track).map(({ ev, code }) => {
                        const cp = userProfile?.competition_profile || {};
                        const viewPb = cp.personal_bests || {};
                        const viewSb = cp.season_bests || {};
                        const sbVal = currentSeason && seasonBests[currentSeason] ? seasonBests[currentSeason][code] : (seasonBests[code] ?? '');
                        const viewSbVal = currentSeason && viewSb[currentSeason] ? viewSb[currentSeason][code] : (viewSb[code] ?? '');
                        const pbTime = parseTime(personalBests[code] || '');
                        const sbTime = parseTime(sbVal);
                        const sk = currentSeason || 'current';
                        const setPb = (part, val) => { const v = String(val).replace(/\D/g, '').slice(0, part === 'ms' ? 2 : 2); setPersonalBests((prev) => ({ ...prev, [code]: formatTime({ ...parseTime(prev[code]), [part]: v }, { pad: false }) })); };
                        const setSb = (part, val) => { const v = String(val).replace(/\D/g, '').slice(0, part === 'ms' ? 2 : 2); setSeasonBests((prev) => ({ ...prev, [sk]: { ...(prev[sk] || {}), [code]: formatTime({ ...parseTime((prev[sk] && prev[sk][code]) || prev[code] || ''), [part]: v }, { pad: false }) } })); };
                        if (!isPbSbEditing) {
                          return (
                            <div key={code} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-900">{toProperCase(ev.event || code)}</span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <span className="block text-xs font-medium text-gray-500 mb-1">PB</span>
                                  <span className="text-sm font-mono text-gray-900">{formatTimeForDisplay(viewPb[code])}</span>
                                </div>
                                <div>
                                  <span className="block text-xs font-medium text-gray-500 mb-1">SB</span>
                                  <span className="text-sm font-mono text-gray-900">{formatTimeForDisplay(viewSbVal)}</span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={code} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                            <div className="flex items-center justify-between mb-4">
                              <span className="text-sm font-medium text-gray-900">{toProperCase(ev.event || code)}</span>
                              <button type="button" onClick={() => handleRemovePbSb(code)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100" title="Remove"><XMarkIcon className="h-5 w-5" /></button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">PB (hh:mm:ss.ms)</label>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <input type="text" inputMode="numeric" placeholder="hh" maxLength={2} value={pbTime.hh} onChange={(e) => setPb('hh', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Hours" />
                                  <input type="text" inputMode="numeric" placeholder="mm" maxLength={2} value={pbTime.mm} onChange={(e) => setPb('mm', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Minutes" />
                                  <input type="text" inputMode="numeric" placeholder="ss" maxLength={2} value={pbTime.ss} onChange={(e) => setPb('ss', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Seconds" />
                                  <input type="text" inputMode="numeric" placeholder="ms" maxLength={2} value={pbTime.ms} onChange={(e) => setPb('ms', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Hundredths" />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">SB (hh:mm:ss.ms)</label>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <input type="text" inputMode="numeric" placeholder="hh" maxLength={2} value={sbTime.hh} onChange={(e) => setSb('hh', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Hours" />
                                  <input type="text" inputMode="numeric" placeholder="mm" maxLength={2} value={sbTime.mm} onChange={(e) => setSb('mm', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Minutes" />
                                  <input type="text" inputMode="numeric" placeholder="ss" maxLength={2} value={sbTime.ss} onChange={(e) => setSb('ss', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Seconds" />
                                  <input type="text" inputMode="numeric" placeholder="ms" maxLength={2} value={sbTime.ms} onChange={(e) => setSb('ms', e.target.value)} className="min-w-[2.75rem] w-12 px-2 py-2.5 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Hundredths" />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {(isPbSbEditing ? pbSbEntries.field : pbSbEntriesFromProfile.field).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Field</h3>
                    <div className="space-y-4">
                      {(isPbSbEditing ? pbSbEntries.field : pbSbEntriesFromProfile.field).map(({ ev, code }) => {
                        const cp = userProfile?.competition_profile || {};
                        const viewPb = cp.personal_bests || {};
                        const viewSb = cp.season_bests || {};
                        const sbVal = currentSeason && seasonBests[currentSeason] ? seasonBests[currentSeason][code] : (seasonBests[code] ?? '');
                        const viewSbVal = currentSeason && viewSb[currentSeason] ? viewSb[currentSeason][code] : (viewSb[code] ?? '');
                        if (!isPbSbEditing) {
                          return (
                            <div key={code} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-900">{toProperCase(ev.event || code)}</span>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <span className="block text-xs font-medium text-gray-500 mb-1">PB</span>
                                  <span className="text-sm font-mono text-gray-900">{formatTimeForDisplay(viewPb[code])} m</span>
                                </div>
                                <div>
                                  <span className="block text-xs font-medium text-gray-500 mb-1">SB</span>
                                  <span className="text-sm font-mono text-gray-900">{formatTimeForDisplay(viewSbVal)} m</span>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div key={code} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                            <div className="flex items-center justify-between mb-4">
                              <span className="text-sm font-medium text-gray-900">{toProperCase(ev.event || code)}</span>
                              <button type="button" onClick={() => handleRemovePbSb(code)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100" title="Remove"><XMarkIcon className="h-5 w-5" /></button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">PB (m)</label>
                                <input type="number" step="0.01" min="0" value={personalBests[code] || ''} onChange={(e) => setPersonalBests((prev) => ({ ...prev, [code]: e.target.value }))} placeholder="e.g. 6.50" className="w-full max-w-[120px] px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-2">SB (m)</label>
                                <input type="number" step="0.01" min="0" value={sbVal} onChange={(e) => { const sk = currentSeason || 'current'; setSeasonBests((prev) => ({ ...prev, [sk]: { ...(prev[sk] || {}), [code]: e.target.value } })); }} placeholder="e.g. 6.50" className="w-full max-w-[120px] px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {events.length > 0 && (isPbSbEditing ? eventCodesWithData.size === 0 : eventCodesWithDataFromProfile.size === 0) && (
                <p className="text-sm text-gray-500">{isPbSbEditing ? 'No PB/SB added yet. Use the form above to add events.' : 'No PB/SB saved yet. Click Edit to add.'}</p>
              )}
              {events.length === 0 && (
                <p className="text-sm text-gray-500">Loading events…</p>
              )}
              {isPbSbEditing && (
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSavePbSb}
                  disabled={savingPbSb}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingPbSb ? 'Saving…' : 'Save PB & SB'}
                </button>
                <p className="text-xs text-gray-500">
                  Saves all personal bests and season bests listed above.
                </p>
              </div>
              )}
            </div>
          </div>

          {/* My Results */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-primary-600" />
              My Results
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Log your competition results to track your season&apos;s best. Selecting a season when adding a result updates your SB for that season.
            </p>
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowAddResult(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add result
              </button>
            </div>
            {showAddResult && (
              <form onSubmit={handleAddResultSubmit} className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Log a result</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Competition</label>
                    <select
                      value={addResultForm.competition_id}
                      onChange={(e) => setAddResultForm((p) => ({ ...p, competition_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      <option value="">Select competition</option>
                      {competitionsList.map((c) => (
                        <option key={c.id} value={c.id}>{c.name || c.id}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
                    <select
                      value={addResultForm.event_code}
                      onChange={(e) => setAddResultForm((p) => ({ ...p, event_code: e.target.value, result_value: '' }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      <option value="">Select event</option>
                      <optgroup label="Track">
                        {trackEvents.map((ev) => (
                          <option key={ev.event_code || ev.event} value={ev.event_code || ev.event}>{toProperCase(ev.event || ev.event_code)}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Field">
                        {fieldEvents.map((ev) => (
                          <option key={ev.event_code || ev.event} value={ev.event_code || ev.event}>{toProperCase(ev.event || ev.event_code)}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <div>
                    {(() => {
                      const selectedEv = events.find((e) => (e.event_code || e.event) === addResultForm.event_code);
                      const resultIsField = selectedEv && isFieldEvent(selectedEv);
                      const resultTime = parseTime(addResultForm.result_value || '');
                      return resultIsField ? (
                        <>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Result (number only, no unit)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={addResultForm.result_value}
                            onChange={(e) => setAddResultForm((p) => ({ ...p, result_value: e.target.value }))}
                            placeholder="e.g. 6.50"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                            required
                          />
                        </>
                      ) : (
                        <>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Result (hh:mm:ss.ms)</label>
                          <div className="flex items-center gap-2 flex-wrap">
                            <input type="text" inputMode="numeric" placeholder="hh" maxLength={2} value={resultTime.hh} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddResultForm((p) => ({ ...p, result_value: formatTime({ ...parseTime(p.result_value), hh: v }, { pad: false }) })); }} className="min-w-[2.75rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Hours" />
                            <input type="text" inputMode="numeric" placeholder="mm" maxLength={2} value={resultTime.mm} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddResultForm((p) => ({ ...p, result_value: formatTime({ ...parseTime(p.result_value), mm: v }, { pad: false }) })); }} className="min-w-[2.75rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Minutes" />
                            <input type="text" inputMode="numeric" placeholder="ss" maxLength={2} value={resultTime.ss} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddResultForm((p) => ({ ...p, result_value: formatTime({ ...parseTime(p.result_value), ss: v }, { pad: false }) })); }} className="min-w-[2.75rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Seconds" />
                            <input type="text" inputMode="numeric" placeholder="ms" maxLength={2} value={resultTime.ms} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 2); setAddResultForm((p) => ({ ...p, result_value: formatTime({ ...parseTime(p.result_value), ms: v }, { pad: false }) })); }} className="min-w-[2.75rem] w-12 px-2 py-2 text-sm border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-primary-500 focus:border-primary-500" title="Hundredths" required />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Season</label>
                    <select
                      value={addResultForm.season}
                      onChange={(e) => setAddResultForm((p) => ({ ...p, season: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      <option value="">Select season</option>
                      {seasons.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700">
                    Save result
                  </button>
                  <button type="button" onClick={() => { setShowAddResult(false); setAddResultForm({ competition_id: '', event_code: '', result_value: '', season: '' }); }} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </form>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Competition</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Result</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Season</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {myResults.map((r) => (
                    <tr key={r.id}>
                      <td className="px-3 py-2 text-sm text-gray-900">{r.competition_name || r.competition_id || '—'}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{toProperCase(r.event_code || r.event_name || '')}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatResultForDisplay(r.result_value, { event_code: r.event_code })}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{r.season}</td>
                      <td className="px-3 py-2 text-sm text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {myResults.length === 0 && !showAddResult && (
              <p className="text-sm text-gray-500">No results logged yet. Add a result above.</p>
            )}
          </div>

          {/* Medical Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              Medical Information
            </h2>
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Medical Conditions
                </label>
                <textarea
                  name="medical_conditions"
                  value={formData.medical_conditions}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows={3}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base resize-none"
                  placeholder="List any medical conditions (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Allergies
                </label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  rows={3}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base resize-none"
                  placeholder="List any allergies (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Coach name
                </label>
                <input
                  type="text"
                  name="coach_name"
                  value={formData.coach_name}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed text-sm sm:text-base"
                  placeholder="Assigned coach (required for merchandise purchase)"
                />
                <p className="text-xs text-gray-500 mt-1">Required for purchasing merchandise and competition signups.</p>
              </div>
            </div>
          </div>

          {/* Password Change */}
          {isEditing && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <svg className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Change Password
              </h2>
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="current_password"
                      value={formData.current_password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 sm:py-3 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="Confirm new password"
                  />
                </div>
                <p className="text-xs sm:text-sm text-gray-500">
                  Leave password fields empty if you don't want to change your password.
                </p>
              </div>
            </div>
          )}
        </div>
        </div>
      </section>
    </div>
  );
};

export default Profile;